import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Camera, Save, Lock, User, MapPin, CreditCard, Store, Navigation, Eye, EyeOff, Upload, FileText, Trash2, X, Search, Map, Truck, ShoppingCart, CheckCircle2, Circle, ShieldAlert } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { sellerAPI } from '../api';
import { updateSellerProfile } from '../store/authSlice';
import { RootState } from '../store/store';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import axios from 'axios';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../utils/googleMapsConfig';
import Webservice from './../Webservice/Webservice';
import imageCompression from 'browser-image-compression';
import MD5 from 'crypto-js/md5';

// Cleanly split address components
const parseAddressComponents = (components: any) => {
  let result = { houseNumber: '', areaOrStreet: '', villageOrLocality: '', landmark: '', city: '', state: '', country: '', postalCode: '' };
  if (!components || !Array.isArray(components)) return result;

  let streetNumber = ''; let route = ''; let sublocality = ''; let neighborhood = '';

  components.forEach((component) => {
    const types = component.types;
    if (types.includes('street_number')) streetNumber = component.long_name;
    else if (types.includes('route')) route = component.long_name;
    else if (types.includes('sublocality') || types.includes('sublocality_level_1')) sublocality = component.long_name;
    else if (types.includes('neighborhood')) neighborhood = component.long_name;
    else if (types.includes('locality')) result.city = component.long_name;
    else if (types.includes('administrative_area_level_2') && !result.city) result.city = component.long_name;
    else if (types.includes('administrative_area_level_1')) result.state = component.long_name;
    else if (types.includes('country')) result.country = component.long_name;
    else if (types.includes('postal_code')) result.postalCode = component.long_name;
  });

  result.houseNumber = streetNumber;
  result.areaOrStreet = [route, neighborhood].filter(Boolean).join(', ');
  result.villageOrLocality = sublocality;
  return result;
};

interface TabBtnProps {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<any>;
}

const TabBtn = ({ label, active, onClick, icon: Icon }: TabBtnProps) => (
  <button onClick={onClick} style={{
    padding: '8px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer',
    background: active ? '#14b8a6' : 'transparent',
    color: active ? 'white' : '#64748b', fontWeight: '600', fontSize: '13px',
    display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s ease',
    whiteSpace: 'nowrap', flexShrink: 0,
  }}>
    <Icon size={15} />{label}
  </button>
);

export default function ProfilePage() {
  const { seller: authSeller } = useSelector((s: RootState) => s.auth);
  const [seller, setSeller] = useState(authSeller);
  let Verified = false
  let reSubmit = false
  if (seller?.verification_status === "A") {
    Verified = true
  }
  if (seller?.verification_status === "R") {
    reSubmit = true
  }
  // const Verified = seller?.verification_status === "A" || seller?.verification_status === "P";

  const isBlocked = seller?.isBlock === true;

  const isStoreInfoFilled = !!(
    seller?.storeinfo?.store_name &&
    seller?.storeinfo?.phone_number &&
    seller?.storeinfo?.owner_name &&
    seller?.storeinfo?.businessLicense?.length > 0 &&
    seller?.storeinfo?.panCard?.length > 0
  );
  const isAddressFilled = !!(
    seller?.address?.areaOrStreet &&
    seller?.address?.city &&
    seller?.address?.villageOrLocality &&
    seller?.address?.fullAddress &&
    seller?.temporaryLocation &&
    seller?.address?.state &&
    seller?.address?.postalCode
  );
  const isBankFilled = !!(
    seller?.bankdetails?.account_holder_name &&
    seller?.bankdetails?.account_number &&
    seller?.bankdetails?.ifsc_code &&
    seller?.bankdetails?.bank_name
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(location.state?.activeTab || 'store');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoPreview, setLogoPreview] = useState(seller?.storeLogo || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [tempLogoFile, setTempLogoFile] = useState<File | null>(null);
  const [tempLogoPreview, setTempLogoPreview] = useState<string>('');

  useEffect(() => {
    if (isImageModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);

  const [businessLicenseFiles, setBusinessLicenseFiles] = useState<{ file: File, preview: string }[]>([]);
  const [businessLicensePreviews, setBusinessLicensePreviews] = useState<string[]>(Array.isArray(seller?.storeinfo?.businessLicense) ? seller.storeinfo.businessLicense : (seller?.storeinfo?.businessLicense ? [seller.storeinfo.businessLicense] : []));
  const [panCardFiles, setPanCardFiles] = useState<{ file: File, preview: string }[]>([]);
  const [panCardPreviews, setPanCardPreviews] = useState<string[]>(Array.isArray(seller?.storeinfo?.panCard) ? seller.storeinfo.panCard : (seller?.storeinfo?.panCard ? [seller.storeinfo.panCard] : []));

  const truncateFileName = (name: string) => name.length > 20 ? name.slice(0, 20) + '...' : name;

  const handleViewDocument = (url: string, file: File | null) => {
    if (!url) return;
    const isPdf = file ? file.type === 'application/pdf' : url.split('?')[0].toLowerCase().endsWith('.pdf');

    if (isPdf) {
      window.open(url, '_blank');
    } else {
      Swal.fire({
        html: `<img src="${url}" alt="Document Preview" style="max-width: 100%; max-height: 90vh; object-fit: contain; margin: 0 auto; display: block;" />`,
        showCloseButton: true,
        showConfirmButton: false,
        background: "transparent",
        backdrop: "rgba(0,0,0,0.85)",
        width: "90vw",
        padding: "0",
        customClass: {
          popup: 'custom-swal-popup',
          closeButton: 'custom-swal-close'
        },
      });
    }
  };

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [mapCenter, setMapCenter] = useState({ lat: 13.0827, lng: 80.2707 });

  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const geocoderRef = useRef(null);
  const placesServiceRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapIdleDebounceRef = useRef(null);
  const lastGeocodedPosRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const onPlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo({ lat, lng });
          mapInstanceRef.current.setZoom(17);
        }
        lastGeocodedPosRef.current = null;
        reverseGeocode(lat, lng);
      }
    }
  };

  const fallbackWithPlacesAPI = async (lat, lng) => {
    return new Promise((resolve) => {
      if (!window.google) { resolve(false); return; }
      if (!placesServiceRef.current) {
        const dummyDiv = document.createElement('div');
        placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current || dummyDiv);
      }
      const location = new window.google.maps.LatLng(lat, lng);
      placesServiceRef.current.nearbySearch({ location, radius: 50 }, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          placesServiceRef.current.getDetails({ placeId: results[0].place_id, fields: ['address_components', 'formatted_address'] }, (place, detailStatus) => {
            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place && place.address_components) {
              const parsed = parseAddressComponents(place.address_components);
              updateProfileAddress(place.formatted_address, parsed);
              resolve(true);
            } else resolve(false);
          });
        } else resolve(false);
      });
    });
  };

  const reverseGeocode = (lat, lng) => {
    if (!window.google) return;
    if (!geocoderRef.current) geocoderRef.current = new window.google.maps.Geocoder();
    const latLng = new window.google.maps.LatLng(lat, lng);
    geocoderRef.current.geocode({ location: latLng }, async (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const parsed = parseAddressComponents(results[0].address_components);
        updateProfileAddress(results[0].formatted_address, parsed);
      } else {
        const placesSuccess = await fallbackWithPlacesAPI(lat, lng);
        if (!placesSuccess) toast.error(`Google Maps API Failed: ${status}`);
      }
    });
  };

  const updateProfileAddress = (fullAddress, parsed) => {
    setSearchValue(fullAddress);
    setErrors(prev => ({
      ...prev,
      street: '',
      city: '',
      state: '',
      pincode: '',
      villageOrLocality: '',
      fullAddress: ''
    }));
    setProfile(p => {
      const currentPerm = p.businessAddress.permanentLocation || '';
      return {
        ...p,
        businessAddress: {
          ...p.businessAddress,
          street: [parsed.houseNumber, parsed.areaOrStreet].filter(Boolean).join(', '),
          villageOrLocality: parsed.villageOrLocality,
          city: parsed.city,
          state: parsed.state,
          pincode: parsed.postalCode,
          fullAddress: fullAddress,
          temporaryLocation: fullAddress,
          permanentLocation: currentPerm === 'Location Skipped' ? '' : currentPerm
        }
      };
    });
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    toast.loading('Detecting location...', { id: 'geo-toast' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        lastGeocodedPosRef.current = null;
        setMapCenter({ lat, lng });
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo({ lat, lng });
          mapInstanceRef.current.setZoom(18);
        }
        reverseGeocode(lat, lng);
        setLocating(false);
        toast.success('Location detected!', { id: 'geo-toast' });
      },
      (err) => {
        setLocating(false);
        toast.error('Could not get precise location. Try moving the map manually.', { id: 'geo-toast' });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [profile, setProfile] = useState({
    storeName: '', ownerName: '', phone: '', gstNumber: '',
    businessAddress: { street: '', city: '', state: '', pincode: '', villageOrLocality: '', temporaryLocation: '', permanentLocation: '', fullAddress: '' },
  });
  const [bank, setBank] = useState({ account_holder_name: '', bank_name: '', account_number: '', confirm_account_number: '', ifsc_code: '' });
  const [savedBank, setSavedBank] = useState({ account_holder_name: '', bank_name: '', account_number: '', ifsc_code: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [platformRateCard, setPlatformRateCard] = useState({
    shippingBaseFee: 0,
    shippingTier0_5: 0,
    shippingTier5_10: 0,
    shippingTier10Plus: 0,
    shippingPerKgRate: 0,
    maxDeliveryDays: 2
  });
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showConfirmAccountNumber, setShowConfirmAccountNumber] = useState(false);
  const [shippingRules, setShippingRules] = useState({
    freeShippingMinOrderValue: 0,
    freeShippingMaxDistance: 0,
    deliveryDays: 2
  });
  const [isMinOrderEnabled, setIsMinOrderEnabled] = useState(false);
  const [isMaxDistanceEnabled, setIsMaxDistanceEnabled] = useState(false);
  const [loadingMinOrder, setLoadingMinOrder] = useState(false);
  const [loadingMaxDistance, setLoadingMaxDistance] = useState(false);

  const [isLoadingIfsc, setIsLoadingIfsc] = useState(false);
  const [ifscError, setIfscError] = useState('');

  useEffect(() => {
    const cleanIfsc = bank.ifsc_code.trim().toUpperCase();
    if (cleanIfsc.length === 11) {
      setIsLoadingIfsc(true);
      setIfscError('');
      axios.get(`https://ifsc.razorpay.com/${cleanIfsc}`)
        .then(res => {
          setBank(b => ({ ...b, bank_name: res.data.BANK || '' }));
          setErrors(prev => ({ ...prev, bank_name: '' }));
        })
        .catch(err => {
          setBank(b => ({ ...b, bank_name: '' }));
          setIfscError('Invalid IFSC Code. Please check and try again.');
        })
        .finally(() => setIsLoadingIfsc(false));
    } else {
      setBank(b => ({ ...b, bank_name: '' }));
      setIfscError('');
    }
  }, [bank.ifsc_code]);

  const calculateStrength = (pass) => {
    if (!pass) return 0;
    let score = 0;
    if (pass.length >= 8) score += 1;
    if (/[A-Z]/.test(pass)) score += 1;
    if (/\d/.test(pass)) score += 1;
    if (/[!@#$%^&*]/.test(pass)) score += 1;
    return score;
  };

  const getStrengthColor = (score) => {
    if (score === 0) return '#e2e8f0';
    if (score === 1) return '#ef4444'; // Weak
    if (score === 2) return '#f59e0b'; // Fair
    if (score === 3) return '#3b82f6'; // Good
    if (score === 4) return '#10b981'; // Strong
  };

  const getStrengthLabel = (score) => {
    if (score === 0) return '';
    if (score === 1) return 'Weak';
    if (score === 2) return 'Fair';
    if (score === 3) return 'Good';
    if (score === 4) return 'Strong';
  };

  useEffect(() => {
    sellerAPI.getProfile().then(res => {
      const s = res.data.seller;
      setSeller(s);
      setProfile({
        storeName: s.storeinfo?.store_name || '',
        ownerName: s.storeinfo?.owner_name || '',
        phone: s.storeinfo?.phone_number || '',
        gstNumber: s.storeinfo?.gst_number || '',
        businessAddress: s.address ? {
          street: s.address.areaOrStreet || '',
          city: s.address.city || '',
          state: s.address.state || '',
          pincode: s.address.postalCode || '',
          villageOrLocality: s.address.villageOrLocality || '',
          fullAddress: s.address.fullAddress === 'Location Skipped' ? '' : s.address.fullAddress,
          temporaryLocation: (s.address?.temporaryLocation || s.temporaryLocation) === 'Location Skipped' ? '' : (s.address?.temporaryLocation || s.temporaryLocation || ''),
          permanentLocation: (s.address?.permanentLocation || s.permanentLocation) === 'Location Skipped' ? '' : (s.address?.permanentLocation || s.permanentLocation || '')
        } : { street: '', city: '', state: '', pincode: '', villageOrLocality: '', temporaryLocation: '', permanentLocation: '', fullAddress: '' },
      });
      setBank(s.bankdetails ? { ...s.bankdetails, confirm_account_number: s.bankdetails.account_number } : { account_holder_name: '', bank_name: '', account_number: '', confirm_account_number: '', ifsc_code: '' });
      setSavedBank(s.bankdetails || { account_holder_name: '', bank_name: '', account_number: '', ifsc_code: '' });
      setShippingRules({
        freeShippingMinOrderValue: s.shippingRules?.freeShippingMinOrderValue || 0,
        freeShippingMaxDistance: s.shippingRules?.freeShippingMaxDistance || 0,
        deliveryDays: s.shippingRules?.deliveryDays || 2
      });
      setIsMinOrderEnabled((s.shippingRules?.freeShippingMinOrderValue || 0) > 0);
      setIsMaxDistanceEnabled((s.shippingRules?.freeShippingMaxDistance || 0) > 0);
      setLogoPreview(s.storeLogo || '');
      const bl = s.storeinfo?.businessLicense;
      setBusinessLicensePreviews(Array.isArray(bl) ? bl : (bl ? [bl] : []));
      setBusinessLicenseFiles([]);
      const pan = s.storeinfo?.panCard;
      setPanCardPreviews(Array.isArray(pan) ? pan : (pan ? [pan] : []));
      setPanCardFiles([]);

      if (s.address && s.address.location && s.address.location.coordinates) {
        setMapCenter({
          lat: s.address.location.coordinates[1],
          lng: s.address.location.coordinates[0]
        });
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (tab === 'location') {
      sellerAPI.getProfile().then(res => {
        const s = res.data.seller;
        setSeller(s);
        if (s && s.address) {
          setProfile(p => ({
            ...p,
            businessAddress: {
              street: s.address.areaOrStreet || '',
              city: s.address.city || '',
              state: s.address.state || '',
              pincode: s.address.postalCode || '',
              fullAddress: s.address.fullAddress === 'Location Skipped' ? '' : s.address.fullAddress,
              villageOrLocality: s.address.villageOrLocality || '',
              temporaryLocation: (s.address?.temporaryLocation || s.temporaryLocation) === 'Location Skipped' ? '' : (s.address?.temporaryLocation || s.temporaryLocation || ''),
              permanentLocation: (s.address?.permanentLocation || s.permanentLocation) === 'Location Skipped' ? '' : (s.address?.permanentLocation || s.permanentLocation || '')
            },
          }));

          if (s.address.location && s.address.location.coordinates) {
            setMapCenter({
              lat: s.address.location.coordinates[1],
              lng: s.address.location.coordinates[0]
            });
          }
        }
      }).catch(() => { });
    } else if (tab === 'shipping') {
      sellerAPI.getPlatformFees().then(res => {
        if (res.data.success) {
          setPlatformRateCard({
            shippingBaseFee: res.data.data.shippingBaseFee || 0,
            shippingTier0_5: res.data.data.shippingTier0_5 || 0,
            shippingTier5_10: res.data.data.shippingTier5_10 || 0,
            shippingTier10Plus: res.data.data.shippingTier10Plus || 0,
            shippingPerKgRate: res.data.data.shippingPerKgRate || 0,
            maxDeliveryDays: res.data.data.maxDeliveryDays || 2
          });
        }
      }).catch(err => console.error("Error fetching platform rate card:", err));
    }
  }, [tab]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] }, maxFiles: 1,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast.error('Please upload a valid image file (PNG, JPG, WEBP).');
        return;
      }
      if (acceptedFiles.length > 0) {
        let file = acceptedFiles[0];
        if (file.type.startsWith('image/') && file.size > 5 * 1024 * 1024) {
          try {
            file = await imageCompression(file, { maxSizeMB: 4.5, maxWidthOrHeight: 2048, useWebWorker: true }) as any;
          } catch (err) {
            console.error("Compression error:", err);
            toast.error(`Failed to compress logo`);
          }
        }
        setLogoFile(file as any);
        setLogoPreview(URL.createObjectURL(file));
      }
    },
  });



  const toTitleCase = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const key = name.split('.')[1];
      setErrors(prev => ({ ...prev, [key]: '' }));

      if (key === 'pincode') {
        let filtered = value.replace(/[^0-9]/g, '').slice(0, 6);
        setProfile(p => ({ ...p, businessAddress: { ...p.businessAddress, [key]: filtered } }));
      } else {
        setProfile(p => ({ ...p, businessAddress: { ...p.businessAddress, [key]: value } }));
      }
    } else if (name === 'storeName') {
      setErrors(prev => ({ ...prev, [name]: '' }));
      // letter and number only (and spaces), max 50 characters
      let filtered = value.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 50);
      setProfile(p => ({ ...p, [name]: toTitleCase(filtered) }));
    } else if (name === 'ownerName') {
      setErrors(prev => ({ ...prev, [name]: '' }));
      // letter only (and spaces), max 40 characters
      let filtered = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 40);
      setProfile(p => ({ ...p, [name]: toTitleCase(filtered) }));
    } else if (name === 'phone') {
      setErrors(prev => ({ ...prev, [name]: '' }));
      // 10 digits only
      let filtered = value.replace(/[^0-9]/g, '').slice(0, 10);
      setProfile(p => ({ ...p, [name]: filtered }));
    } else if (name === 'gstNumber') {
      setErrors(prev => ({ ...prev, [name]: '' }));
      // letter and number only, exactly 15 characters
      let filtered = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15);
      setProfile(p => ({ ...p, [name]: filtered }));
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
      setProfile(p => ({ ...p, [name]: value }));
    }
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'account_holder_name') {
      // letter only (and spaces), max 40 characters, first letter caps (Title Case)
      let filtered = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 40);
      setBank(b => ({ ...b, [name]: toTitleCase(filtered) }));
    } else if (name === 'bank_name') {
      // letter only (and spaces), max 60 characters, first letter caps (Title Case)
      let filtered = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 60);
      setBank(b => ({ ...b, [name]: toTitleCase(filtered) }));
    } else if (name === 'account_number' || name === 'confirm_account_number') {
      // number only, max 30 characters
      let filtered = value.replace(/[^0-9]/g, '').slice(0, 30);
      setBank(b => ({ ...b, [name]: filtered }));
    } else if (name === 'ifsc_code') {
      // letter and number only, max 11 characters, uppercase
      let filtered = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 11);
      setBank(b => ({ ...b, [name]: filtered }));
    } else {
      setBank(b => ({ ...b, [name]: value }));
    }
  };

  const saveBankDetails = async () => {
    let newErrors: Record<string, string> = {};
    if (!bank.account_holder_name?.trim()) newErrors.account_holder_name = 'Account Holder Name is required';
    if (!bank.bank_name?.trim()) newErrors.bank_name = 'Bank Name is required';
    if (!bank.account_number?.trim()) newErrors.account_number = 'Account Number is required';
    if (!bank.confirm_account_number?.trim()) newErrors.confirm_account_number = 'Please confirm Account Number';
    else if (bank.account_number !== bank.confirm_account_number) newErrors.confirm_account_number = 'Account numbers do not match';
    if (!bank.ifsc_code?.trim()) newErrors.ifsc_code = 'IFSC Code is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to update your bank details?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, update it!'
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const payload = {
        userid: seller?._id || seller?.id,
        bankdetails: {
          account_holder_name: bank.account_holder_name,
          bank_name: bank.bank_name,
          account_number: bank.account_number,
          ifsc_code: bank.ifsc_code
        }
      };
      const res = await sellerAPI.updateBankDetails(payload);
      dispatch(updateSellerProfile(res.data.seller));
      setSeller(res.data.seller);
      setSavedBank(res.data.seller.bankdetails || bank);
      if (res.data.seller?.verification_status === 'R') {
        localStorage.setItem('profileUpdatedAfterRejection', 'true');
      }
      Swal.fire({
        icon: 'success', title: 'Bank Details Saved!', text: 'Your bank details have been updated.',
        confirmButtonColor: '#14b8a6', timer: 3000, timerProgressBar: true
      });
    } catch (err) {
      Swal.fire({
        icon: 'error', title: 'Save Failed', text: err.response?.data?.message || 'Unable to save bank details.',
        confirmButtonColor: '#ef4444'
      });
    } finally { setLoading(false); }
  };

  const saveLocation = async () => {
    let newErrors: Record<string, string> = {};
    if (!profile.businessAddress?.street?.trim()) newErrors.street = 'Required field';
    if (!profile.businessAddress?.villageOrLocality?.trim()) newErrors.villageOrLocality = 'Required field';
    if (!profile.businessAddress?.city?.trim()) newErrors.city = 'Required field';
    if (!profile.businessAddress?.state?.trim()) newErrors.state = 'Required field';
    if (!profile.businessAddress?.pincode?.trim()) newErrors.pincode = 'Required field';
    if (!profile.businessAddress?.fullAddress?.trim()) newErrors.fullAddress = 'Required field';

    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      Swal.fire({
        icon: 'error', title: 'Validation Error', text: 'Please fill in all mandatory location fields.',
        confirmButtonColor: '#ef4444'
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to update your location?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, update it!'
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const payload = {
        street: profile.businessAddress.street,
        city: profile.businessAddress.city,
        state: profile.businessAddress.state,
        pincode: profile.businessAddress.pincode,
        villageOrLocality: profile.businessAddress.villageOrLocality,
        fullAddress: profile.businessAddress.fullAddress,
        // temporaryLocation: profile.businessAddress.temporaryLocation,
        // permanentLocation: profile.businessAddress.permanentLocation,
        lat: mapCenter.lat,
        lng: mapCenter.lng,
      };
      const res = await sellerAPI.updateLocation(payload);
      dispatch(updateSellerProfile(res.data.seller));
      setSeller(res.data.seller);
      if (res.data.seller?.verification_status === 'R') {
        localStorage.setItem('profileUpdatedAfterRejection', 'true');
      }
      Swal.fire({
        icon: 'success',
        title: 'Location Saved!',
        text: 'Your store location has been updated successfully.',
        confirmButtonColor: '#14b8a6',
        confirmButtonText: 'Great!',
        timer: 3000,
        timerProgressBar: true,
      }).then(() => {
        setTab('store');
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || 'Unable to save location. Please try again.',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'Try Again',
      });
    } finally { setLoading(false); }
  };

  const clearLocation = () => {
    setProfile(p => ({
      ...p,
      businessAddress: {
        street: '', city: '', state: '', pincode: '',
        villageOrLocality: '', temporaryLocation: '', permanentLocation: '', fullAddress: ""
      }
    }));
    setSearchValue('');
    setMapCenter({ lat: 13.0827, lng: 80.2707 });
    toast('Address cleared. Save to confirm removal.', { icon: '🗑️' });
  };

  const saveStoreInfo = async () => {
    let newErrors: Record<string, string> = {};
    if (!profile.storeName?.trim()) newErrors.storeName = 'Store Name is required';
    if (!profile.ownerName?.trim()) newErrors.ownerName = 'Owner Name is required';

    if (!profile.phone?.trim()) {
      newErrors.phone = 'Phone Number is required';
    } else if (profile.phone.trim().length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!profile.gstNumber?.trim()) {
      newErrors.gstNumber = 'GST Number is required';
    } else {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
      if (!gstRegex.test(profile.gstNumber.trim().toUpperCase())) {
        newErrors.gstNumber = 'Please enter a valid 15-character GST number (e.g. 22AAAAA0000A1Z5)';
      }
    }

    if (businessLicensePreviews.length === 0 && businessLicenseFiles.length === 0) {
      newErrors.businessLicense = 'Business License is required';
    }
    if (panCardPreviews.length === 0 && panCardFiles.length === 0) {
      newErrors.panCard = 'PAN Card is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to update your store information?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, update it!'
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      let payloadStoreInfo: any = {
        store_name: profile.storeName,
        owner_name: profile.ownerName,
        phone_number: profile.phone,
        gst_number: profile.gstNumber,
        businessLicense: businessLicensePreviews,
        panCard: panCardPreviews
      };

      if (businessLicenseFiles.length > 0 || panCardFiles.length > 0) {
        const fd = new FormData();
        fd.append('userid', seller?._id || seller?.id);
        businessLicenseFiles.forEach(f => fd.append('businessLicense', f.file));
        panCardFiles.forEach(f => fd.append('panCard', f.file));

        try {
          const uploadRes = await sellerAPI.uploadDocument(fd);
          const urls = uploadRes.data.urls;
          if (urls.businessLicense) payloadStoreInfo.businessLicense = [...payloadStoreInfo.businessLicense, ...urls.businessLicense];
          if (urls.panCard) payloadStoreInfo.panCard = [...payloadStoreInfo.panCard, ...urls.panCard];
        } catch (uploadErr: any) {
          Swal.fire({
            icon: 'error', title: 'Upload Failed', text: uploadErr.response?.data?.message || 'Failed to upload documents.',
            confirmButtonColor: '#ef4444'
          });
          return;
        }
      }

      const payload = {
        userid: seller?._id || seller?.id,
        storeinfo: payloadStoreInfo
      };

      const res = await sellerAPI.updateStoreInfo(payload);
      dispatch(updateSellerProfile(res.data.seller));
      setSeller(res.data.seller);
      if (res.data.seller?.verification_status === 'R') {
        localStorage.setItem('profileUpdatedAfterRejection', 'true');
      }
      Swal.fire({
        icon: 'success', title: 'Store Info Saved!', text: 'Your store details have been updated.',
        confirmButtonColor: '#14b8a6', timer: 3000, timerProgressBar: true
      }).then(() => {
        setTab('bank');
      });
    } catch (err) {
      Swal.fire({
        icon: 'error', title: 'Save Failed', text: err.response?.data?.message || 'Unable to save store info.',
        confirmButtonColor: '#ef4444'
      });
    } finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('storeName', profile.storeName);
      fd.append('ownerName', profile.ownerName);
      fd.append('phone', profile.phone);
      fd.append('gstNumber', profile.gstNumber);
      fd.append('businessAddress', JSON.stringify(profile.businessAddress));
      fd.append('bankDetails', JSON.stringify(bank));
      if (logoFile) fd.append('storeLogo', logoFile);
      const res = await sellerAPI.updateProfile(fd);
      dispatch(updateSellerProfile(res.data.seller));
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setLoading(false); }
  };

  const savePassword = async () => {
    // Validation: All fields required
    if (!passwords.currentPassword.trim()) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Current password is required', confirmButtonColor: '#ef4444' });
      return;
    }
    if (!passwords.newPassword.trim()) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'New password is required', confirmButtonColor: '#ef4444' });
      return;
    }
    if (!passwords.confirmPassword.trim()) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Confirm new password is required', confirmButtonColor: '#ef4444' });
      return;
    }
    // Validation: Min 8 chars
    if (passwords.newPassword.length < 8) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'New password must be at least 8 characters', confirmButtonColor: '#ef4444' });
      return;
    }
    // Validation: Current != New
    if (passwords.currentPassword === passwords.newPassword) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'New password must be different from current password', confirmButtonColor: '#ef4444' });
      return;
    }
    // Validation: New == Confirm
    if (passwords.newPassword !== passwords.confirmPassword) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'New password and confirm password do not match', confirmButtonColor: '#ef4444' });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to change your password?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, change it!'
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      await sellerAPI.changePassword({
        currentPassword: MD5(passwords.currentPassword).toString(),
        newPassword: MD5(passwords.newPassword).toString()
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      Swal.fire({
        icon: 'success',
        title: 'Password Changed!',
        text: 'Your password has been updated successfully.',
        confirmButtonColor: '#14b8a6',
        timer: 3000,
        timerProgressBar: true
      });
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Unable to change password. Please try again.';
      if (errorMsg === "Current password is incorrect") {
        setErrors(prev => ({ ...prev, currentPassword: errorMsg }));
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Password Change Failed',
          text: errorMsg,
          confirmButtonColor: '#ef4444'
        });
      }
    } finally { setLoading(false); }
  };

  const handleShippingRulesChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value.replace(/[^0-9]/g, '');
    
    if (filteredValue.length > 1 && filteredValue.startsWith('0')) {
      filteredValue = filteredValue.replace(/^0+/, '');
    }

    if (name === 'deliveryDays') {
      if (filteredValue !== '') {
        let numVal = Number(filteredValue);
        if (numVal === 0) {
          filteredValue = '';
        } else if (platformRateCard.maxDeliveryDays && numVal > platformRateCard.maxDeliveryDays) {
          filteredValue = String(platformRateCard.maxDeliveryDays);
        }
      }
    } else {
      if (filteredValue.length > 5) {
        filteredValue = filteredValue.slice(0, 5);
      }
    }
    
    setShippingRules(prev => ({ ...prev, [name]: filteredValue === '' ? '' : Number(filteredValue) }));
  };

  const handleToggleMinOrder = async () => {
    if (isMinOrderEnabled) {
      setIsMinOrderEnabled(false);
      setShippingRules(prev => ({ ...prev, freeShippingMinOrderValue: 0 }));

      setLoadingMinOrder(true);
      try {
        const payload = {
          userid: seller?._id || seller?.id,
          shippingRules: {
            freeShippingMinOrderValue: 0,
            freeShippingMaxDistance: Number(shippingRules.freeShippingMaxDistance)
          }
        };
        const res = await sellerAPI.updateShippingRules(payload);
        dispatch(updateSellerProfile(res.data.seller));
        Swal.fire({
          icon: 'success', title: 'Disabled!', text: 'Minimum Order Value rule disabled.',
          confirmButtonColor: '#14b8a6', timer: 2000, timerProgressBar: true
        });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Unable to save rule.', confirmButtonColor: '#ef4444' });
        setIsMinOrderEnabled(true);
        setShippingRules(prev => ({ ...prev, freeShippingMinOrderValue: shippingRules.freeShippingMinOrderValue }));
      } finally { setLoadingMinOrder(false); }
    } else {
      Swal.fire({
        icon: 'info',
        title: 'Action Required',
        text: 'Please enter an amount below and click Save Settings to enable this rule.',
        confirmButtonColor: '#14b8a6'
      });
    }
  };

  const handleToggleMaxDistance = async () => {
    if (isMaxDistanceEnabled) {
      setIsMaxDistanceEnabled(false);
      setShippingRules(prev => ({ ...prev, freeShippingMaxDistance: 0 }));

      setLoadingMaxDistance(true);
      try {
        const payload = {
          userid: seller?._id || seller?.id,
          shippingRules: {
            freeShippingMinOrderValue: Number(shippingRules.freeShippingMinOrderValue),
            freeShippingMaxDistance: 0
          }
        };
        const res = await sellerAPI.updateShippingRules(payload);
        dispatch(updateSellerProfile(res.data.seller));
        Swal.fire({
          icon: 'success', title: 'Disabled!', text: 'Maximum Delivery Radius rule disabled.',
          confirmButtonColor: '#14b8a6', timer: 2000, timerProgressBar: true
        });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Unable to save rule.', confirmButtonColor: '#ef4444' });
        setIsMaxDistanceEnabled(true);
        setShippingRules(prev => ({ ...prev, freeShippingMaxDistance: shippingRules.freeShippingMaxDistance }));
      } finally { setLoadingMaxDistance(false); }
    } else {
      Swal.fire({
        icon: 'info',
        title: 'Action Required',
        text: 'Please enter a radius below and click Save Settings to enable this rule.',
        confirmButtonColor: '#14b8a6'
      });
    }
  };

  const saveMinOrderRule = async () => {
    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to update the Minimum Order Value rule?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, update it!'
    });
    if (!confirm.isConfirmed) return;

    setLoadingMinOrder(true);
    try {
      const val = Number(shippingRules.freeShippingMinOrderValue);
      const payload = {
        userid: seller?._id || seller?.id,
        shippingRules: {
          freeShippingMinOrderValue: val,
          freeShippingMaxDistance: Number(shippingRules.freeShippingMaxDistance),
          deliveryDays: Number(shippingRules.deliveryDays)
        }
      };
      const res = await sellerAPI.updateShippingRules(payload);
      dispatch(updateSellerProfile(res.data.seller));

      if (val > 0) {
        setIsMinOrderEnabled(true);
      } else {
        setIsMinOrderEnabled(false);
      }

      Swal.fire({
        icon: 'success', title: 'Saved!', text: 'Minimum Order Value rule updated.',
        confirmButtonColor: '#14b8a6', timer: 2000, timerProgressBar: true
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Unable to save rule.', confirmButtonColor: '#ef4444' });
    } finally { setLoadingMinOrder(false); }
  };

  const saveMaxDistanceRule = async () => {
    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to update the Maximum Delivery Radius?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, update it!'
    });
    if (!confirm.isConfirmed) return;

    setLoadingMaxDistance(true);
    try {
      const val = Number(shippingRules.freeShippingMaxDistance);
      const payload = {
        userid: seller?._id || seller?.id,
        shippingRules: {
          freeShippingMinOrderValue: Number(shippingRules.freeShippingMinOrderValue),
          freeShippingMaxDistance: val,
          deliveryDays: Number(shippingRules.deliveryDays)
        }
      };
      const res = await sellerAPI.updateShippingRules(payload);
      dispatch(updateSellerProfile(res.data.seller));

      if (val > 0) {
        setIsMaxDistanceEnabled(true);
      } else {
        setIsMaxDistanceEnabled(false);
      }

      Swal.fire({
        icon: 'success', title: 'Saved!', text: 'Maximum Delivery Radius rule updated.',
        confirmButtonColor: '#14b8a6', timer: 2000, timerProgressBar: true
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Unable to save rule.', confirmButtonColor: '#ef4444' });
    } finally { setLoadingMaxDistance(false); }
  };

  const saveShippingRules = async () => {
    const confirm = await Swal.fire({
      title: 'Confirm Update',
      text: 'Are you sure you want to save shipping Days?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, save it!'
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const payload = {
        userid: seller?._id || seller?.id,
        shippingRules: {
          freeShippingMinOrderValue: Number(shippingRules.freeShippingMinOrderValue),
          freeShippingMaxDistance: Number(shippingRules.freeShippingMaxDistance),
          deliveryDays: Number(shippingRules.deliveryDays)
        }
      };
      const res = await sellerAPI.updateShippingRules(payload);
      dispatch(updateSellerProfile(res.data.seller));
      Swal.fire({
        icon: 'success', title: 'Shipping Rules Saved!', text: 'Your shipping configuration has been updated.',
        confirmButtonColor: '#14b8a6', timer: 3000, timerProgressBar: true
      });
    } catch (err) {
      Swal.fire({
        icon: 'error', title: 'Save Failed', text: err.response?.data?.message || 'Unable to save shipping rules.',
        confirmButtonColor: '#ef4444'
      });
    } finally { setLoading(false); }
  };

  const storeInfoChanged =
    profile.storeName !== (seller?.storeinfo?.store_name || '') ||
    profile.ownerName !== (seller?.storeinfo?.owner_name || '') ||
    profile.phone !== (seller?.storeinfo?.phone_number || '') ||
    profile.gstNumber !== (seller?.storeinfo?.gst_number || '') ||
    logoFile !== null ||
    businessLicenseFiles.length > 0 ||
    panCardFiles.length > 0 ||
    businessLicensePreviews.length !== (Array.isArray(seller?.storeinfo?.businessLicense) ? seller?.storeinfo?.businessLicense.length : (seller?.storeinfo?.businessLicense ? 1 : 0)) ||
    panCardPreviews.length !== (Array.isArray(seller?.storeinfo?.panCard) ? seller?.storeinfo?.panCard.length : (seller?.storeinfo?.panCard ? 1 : 0));

  const locationChanged =
    profile.businessAddress.fullAddress !== ((seller?.address?.fullAddress || '')) ||
    profile.businessAddress.street !== (seller?.address?.areaOrStreet || '') ||
    profile.businessAddress.city !== (seller?.address?.city || '') ||
    profile.businessAddress.state !== (seller?.address?.state || '') ||
    profile.businessAddress.pincode !== (seller?.address?.postalCode || '') ||
    profile.businessAddress.villageOrLocality !== (seller?.address?.villageOrLocality || '') ||
    profile.businessAddress.temporaryLocation !== ((seller?.address?.temporaryLocation || seller?.temporaryLocation) === 'Location Skipped' ? '' : (seller?.address?.temporaryLocation || seller?.temporaryLocation || '')) ||
    profile.businessAddress.permanentLocation !== ((seller?.address?.permanentLocation || seller?.permanentLocation) === 'Location Skipped' ? '' : (seller?.address?.permanentLocation || seller?.permanentLocation || '')) ||
    (mapCenter.lat !== (seller?.address?.location?.coordinates?.[1] || 20.5937)) ||
    (mapCenter.lng !== (seller?.address?.location?.coordinates?.[0] || 78.9629));

  const bankChanged =
    bank.account_holder_name !== (savedBank.account_holder_name || '') ||
    bank.bank_name !== (savedBank.bank_name || '') ||
    bank.account_number !== (savedBank.account_number || '') ||
    bank.confirm_account_number !== (savedBank.account_number || '') ||
    bank.ifsc_code !== (savedBank.ifsc_code || '');

  const passwordChanged =
    passwords.currentPassword.trim() !== '' ||
    passwords.newPassword.trim() !== '' ||
    passwords.confirmPassword.trim() !== '';

  const minOrderChanged = Number(shippingRules.freeShippingMinOrderValue) !== Number(seller?.shippingRules?.freeShippingMinOrderValue || 0);
  const maxDistanceChanged = Number(shippingRules.freeShippingMaxDistance) !== Number(seller?.shippingRules?.freeShippingMaxDistance || 0);

  const handleStoreInfoClick = () => {
    setTab('store');
    if (!isStoreInfoFilled) {
      const newErrors = { ...errors };
      if (!profile.storeName && !seller?.storeinfo?.store_name) newErrors.storeName = 'Required field';
      if (!profile.phone && !seller?.storeinfo?.phone_number) newErrors.phone = 'Required field';
      if (!profile.ownerName && !seller?.storeinfo?.owner_name) newErrors.ownerName = 'Required field';
      setErrors(newErrors);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const handleLocationClick = () => {
    setTab('location');
    if (!isAddressFilled) {
      const newErrors = { ...errors };
      if (!profile.businessAddress?.street) newErrors.street = 'Required field';
      if (!profile.businessAddress?.city) newErrors.city = 'Required field';
      if (!profile.businessAddress?.villageOrLocality) newErrors.villageOrLocality = 'Required field';
      if (!profile.businessAddress?.fullAddress) newErrors.fullAddress = 'Required field';
      if (!profile.businessAddress?.state) newErrors.state = 'Required field';
      if (!profile.businessAddress?.pincode) newErrors.pincode = 'Required field';
      setErrors(newErrors);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const handleBankClick = () => {
    setTab('bank');
    if (!isBankFilled) {
      const newErrors = { ...errors };
      if (!bank.account_holder_name && !seller?.bankdetails?.account_holder_name) newErrors.account_holder_name = 'Required field';
      if (!bank.account_number && !seller?.bankdetails?.account_number) newErrors.account_number = 'Required field';
      if (!bank.ifsc_code && !seller?.bankdetails?.ifsc_code) newErrors.ifsc_code = 'Required field';
      if (!bank.bank_name && !seller?.bankdetails?.bank_name) newErrors.bank_name = 'Required field';
      setErrors(newErrors);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="fade-in">
      {/* Store Logo Card */}
      <div className="profile-header-card" style={{ padding: '28px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
        <div
          onClick={() => {
            setTempLogoPreview(logoPreview);
            setTempLogoFile(logoFile);
            setIsImageModalOpen(true);
          }}
          style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
        >
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: logoPreview ? 'transparent' : 'linear-gradient(135deg, #14b8a6, #0d9488)', overflow: 'hidden', border: '3px solid #14b8a6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: 'white', fontWeight: '800', fontSize: '28px' }}>{profile.storeName?.[0]?.toUpperCase() || 'S'}</span>
            )}
          </div>
          <div style={{ position: 'absolute', bottom: '2px', right: '2px', width: '24px', height: '24px', background: '#14b8a6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <Camera size={12} color="white" />
          </div>
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ fontWeight: '800', fontSize: '18px', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {(seller?.storeinfo?.store_name || seller?.storeName || 'My Store').length > 25
              ? (seller?.storeinfo?.store_name || seller?.storeName || 'My Store').slice(0, 25) + '...'
              : (seller?.storeinfo?.store_name || seller?.storeName || 'My Store')}
          </h2>
          <p style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{seller?.email}</p>
        </div>

        {/* Verification Small Content on the right */}
        {!(seller?.verification_status === 'A' && seller?.submitted === true) && (
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
            {!(isStoreInfoFilled && isAddressFilled && isBankFilled) && (
              <>

                <div onClick={handleLocationClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  {isAddressFilled ? <CheckCircle2 size={22} color="#16a34a" /> : <Circle size={22} color="#f43f5e" />}
                  <span style={{ fontSize: '11px', fontWeight: '600', color: isAddressFilled ? '#166534' : '#9f1239' }}>Location</span>
                </div>
                <div onClick={handleStoreInfoClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  {isStoreInfoFilled ? <CheckCircle2 size={22} color="#16a34a" /> : <Circle size={22} color="#f43f5e" />}
                  <span style={{ fontSize: '11px', fontWeight: '600', color: isStoreInfoFilled ? '#166534' : '#9f1239' }}>Store Info</span>
                </div>
                <div onClick={handleBankClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  {isBankFilled ? <CheckCircle2 size={22} color="#16a34a" /> : <Circle size={22} color="#f43f5e" />}
                  <span style={{ fontSize: '11px', fontWeight: '600', color: isBankFilled ? '#166534' : '#9f1239' }}>Bank Details</span>
                </div>
              </>
            )}

            {(isStoreInfoFilled && isAddressFilled && isBankFilled) && (
              <button
                onClick={() => {
                  navigate('/dashboard');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#0c4eb9',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(12, 78, 185, 0.2)'
                }}
              >
                Go to Dashboard
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="profile-tabs-wrap" style={{ marginBottom: '24px' }}>
        <div className="profile-tabs-inner" style={{
          display: 'flex', gap: '4px',
          background: 'linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%)',
          borderRadius: '14px', padding: '5px',
          border: '1px solid rgba(20, 184, 166, 0.15)',
          boxShadow: '0 2px 12px rgba(20, 184, 166, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
        }}>
          <TabBtn label="Location" active={tab === 'location'} onClick={() => setTab('location')} icon={MapPin} />
          <TabBtn label="Store Information" active={tab === 'store'} onClick={() => setTab('store')} icon={Store} />
          <TabBtn label="Bank Details" active={tab === 'bank'} onClick={() => setTab('bank')} icon={CreditCard} />
          <TabBtn label="Shipping" active={tab === 'shipping'} onClick={() => setTab('shipping')} icon={Navigation} />
          <TabBtn label="Security" active={tab === 'security'} onClick={() => setTab('security')} icon={Lock} />
        </div>
      </div>

      {/* Tab content */}
      <div className="glass-card profile-tab-content" style={{ padding: '28px' }}>
        {/* Store Info tab */}
        {tab === 'store' && (
          <div>
            {/* Visual Store Info Card */}
            <div style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '1px solid #bfdbfe', borderRadius: '16px', padding: '24px', marginBottom: '24px',
              display: 'flex', alignItems: 'center', gap: '20px'
            }}>
              <div style={{ width: '56px', height: '56px', background: '#3b82f6', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(59,130,246,0.2)', flexShrink: 0 }}>
                <Store size={28} color="white" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                  {
                    (seller?.storeinfo?.store_name || seller?.storeName || 'Store name not given').length > 25
                      ? (seller?.storeinfo?.store_name || seller?.storeName || 'Store name not given').slice(0, 25) + '...'
                      : (seller?.storeinfo?.store_name || seller?.storeName || 'Store name not given')
                  }
                </h3>
                <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
                  Owner: {
                    (seller?.storeinfo?.owner_name || seller?.ownerName || 'Not Available').length > 25
                      ? (seller?.storeinfo?.owner_name || seller?.ownerName || 'Not Available').slice(0, 25) + '...'
                      : (seller?.storeinfo?.owner_name || seller?.ownerName || 'Not Available')
                  } <br />
                  Phone: {seller?.storeinfo?.phone_number || ''}
                  <br />
                  GST: {seller?.storeinfo?.gst_number || 'Not Available'}
                </p>
              </div>
            </div>

            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>General Information</h4>
            <div className="profile-info-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))' }}>
              <div>
                <label className="form-label">Store Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className={`form-input ${errors.storeName ? 'input-error' : ''}`} style={errors.storeName ? { borderColor: '#ef4444' } : {}} name="storeName" value={profile.storeName} onChange={handleProfileChange} placeholder="Enter store name" />
                {errors.storeName && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.storeName}</span>}
              </div>
              <div>
                <label className="form-label">Owner Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className={`form-input ${errors.ownerName ? 'input-error' : ''}`} style={errors.ownerName ? { borderColor: '#ef4444' } : {}} name="ownerName" value={profile.ownerName} onChange={handleProfileChange} placeholder="Enter your full name" />
                {errors.ownerName && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.ownerName}</span>}
              </div>
              <div>
                <label className="form-label">Store Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                <input className={`form-input ${errors.phone ? 'input-error' : ''}`} style={errors.phone ? { borderColor: '#ef4444' } : {}} name="phone" value={profile.phone} onChange={handleProfileChange} placeholder="Enter phone number" />
                {errors.phone && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
              </div>
              <div>
                <label className="form-label">GST Number <span style={{ color: '#ef4444' }}>*</span></label>
                <input className={`form-input ${errors.gstNumber ? 'input-error' : ''}`} style={errors.gstNumber ? { borderColor: '#ef4444' } : {}} name="gstNumber" value={profile.gstNumber} onChange={handleProfileChange} placeholder="Enter GST number" maxLength={15} />
                {errors.gstNumber && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.gstNumber}</span>}
              </div>
            </div>
            {!Verified &&
              <>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px', marginTop: '24px' }}>Documents</h4>
                <div className="profile-info-grid" style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
                  <div style={{ minWidth: 0 }}>
                    <label className="form-label">Business License (Max 2) <span style={{ color: '#ef4444' }}>*</span></label>
                    {errors.businessLicense && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '8px', display: 'block' }}>{errors.businessLicense}</span>}
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      {/* Existing Previews */}
                      {businessLicensePreviews.map((preview, index) => (
                        <div key={`prev-${index}`} style={{ flex: '1 1 200px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => handleViewDocument(preview, null)}
                            onMouseEnter={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '0'; }}
                          >
                            {preview.toLowerCase().includes('.pdf') ? (
                              <FileText size={24} color="#4f46e5" />
                            ) : (
                              <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                              <Eye size={20} color="#fff" />
                            </div>
                          </div>
                          <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: '#64748b', fontWeight: 500 }}>Current Document</p>
                            <span onClick={() => handleViewDocument(preview, null)} title={preview.split('?')[0].split('/').pop() || 'View Document'} style={{ color: '#0f172a', fontSize: '14px', fontWeight: 500, cursor: 'pointer', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {truncateFileName(preview.split('?')[0].split('/').pop() || 'View Document')}
                            </span>
                          </div>
                          {(!Verified || reSubmit) && (
                            <button
                              type="button"
                              onClick={() => {
                                const arr = [...businessLicensePreviews];
                                arr.splice(index, 1);
                                setBusinessLicensePreviews(arr);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                              title="Remove Document"
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}

                      {/* New Files */}
                      {businessLicenseFiles.map((item, index) => (
                        <div key={`file-${index}`} style={{ flex: '1 1 200px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => handleViewDocument(item.preview, item.file)}
                            onMouseEnter={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '0'; }}
                          >
                            {item.file.type.includes('pdf') ? (
                              <FileText size={24} color="#4f46e5" />
                            ) : (
                              <img src={item.preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                              <Eye size={20} color="#fff" />
                            </div>
                          </div>
                          <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: '#64748b', fontWeight: 500 }}>New Document</p>
                            <span onClick={() => handleViewDocument(item.preview, item.file)} title={item.file.name} style={{ color: '#0f172a', fontSize: '14px', fontWeight: 500, cursor: 'pointer', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {truncateFileName(item.file.name)}
                            </span>
                          </div>
                          {(!Verified || reSubmit) && (
                            <button
                              type="button"
                              onClick={() => {
                                const arr = [...businessLicenseFiles];
                                arr.splice(index, 1);
                                setBusinessLicenseFiles(arr);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                              title="Remove Document"
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}

                      {(!Verified || reSubmit) && (businessLicensePreviews.length + businessLicenseFiles.length < 2) && (
                        <label
                          style={{
                            flex: '1 1 200px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px', backgroundColor: '#f8fafc',
                            cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                        >
                          <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                            <Upload size={20} color="#3b82f6" />
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#334155' }}>
                            Click to upload <span style={{ color: '#3b82f6' }}>Business License</span>
                          </span>
                          <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>PNG, JPG (max. 5MB)</span>
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            const imageFiles = files.filter(file => {
                              if (!file.type.startsWith('image/')) {
                                toast.error(`${file.name} is not an image`);
                                return false;
                              }
                              return true;
                            });
                            const availableSlots = 2 - (businessLicensePreviews.length + businessLicenseFiles.length);
                            const selectedFiles = imageFiles.slice(0, availableSlots);

                            const processedFiles = await Promise.all(selectedFiles.map(async (file) => {
                              if (file.size > 5 * 1024 * 1024) {
                                try {
                                  const compressedFile = await imageCompression(file, { maxSizeMB: 4.5, maxWidthOrHeight: 2048, useWebWorker: true });
                                  return { file: compressedFile, preview: URL.createObjectURL(compressedFile) };
                                } catch (error) {
                                  console.error("Compression error:", error);
                                  toast.error(`Failed to compress ${file.name}`);
                                }
                              }
                              return { file, preview: URL.createObjectURL(file) };
                            }));
                            setBusinessLicenseFiles(prev => [...prev, ...processedFiles]);
                            setErrors(prev => ({ ...prev, businessLicense: '' }));
                          }} />
                        </label>
                      )}
                    </div>
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <label className="form-label">PAN Card & GST (Max 2) <span style={{ color: '#ef4444' }}>*</span></label>
                    {errors.panCard && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '8px', display: 'block' }}>{errors.panCard}</span>}
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      {/* Existing Previews */}
                      {panCardPreviews.map((preview, index) => (
                        <div key={`prev-${index}`} style={{ flex: '1 1 200px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => handleViewDocument(preview, null)}
                            onMouseEnter={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '0'; }}
                          >
                            {preview.toLowerCase().includes('.pdf') ? (
                              <FileText size={24} color="#4f46e5" />
                            ) : (
                              <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                              <Eye size={20} color="#fff" />
                            </div>
                          </div>
                          <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: '#64748b', fontWeight: 500 }}>Current Document</p>
                            <span onClick={() => handleViewDocument(preview, null)} title={preview.split('?')[0].split('/').pop() || 'View Document'} style={{ color: '#0f172a', fontSize: '14px', fontWeight: 500, cursor: 'pointer', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {truncateFileName(preview.split('?')[0].split('/').pop() || 'View Document')}
                            </span>
                          </div>
                          {(!Verified || reSubmit) && (
                            <button
                              type="button"
                              onClick={() => {
                                const arr = [...panCardPreviews];
                                arr.splice(index, 1);
                                setPanCardPreviews(arr);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                              title="Remove Document"
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}

                      {/* New Files */}
                      {panCardFiles.map((item, index) => (
                        <div key={`file-${index}`} style={{ flex: '1 1 200px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{ width: '64px', height: '64px', borderRadius: '8px', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => handleViewDocument(item.preview, item.file)}
                            onMouseEnter={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={(e) => { (e.currentTarget.lastElementChild as HTMLElement).style.opacity = '0'; }}
                          >
                            {item.file.type.includes('pdf') ? (
                              <FileText size={24} color="#4f46e5" />
                            ) : (
                              <img src={item.preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                              <Eye size={20} color="#fff" />
                            </div>
                          </div>
                          <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: '#64748b', fontWeight: 500 }}>New Document</p>
                            <span onClick={() => handleViewDocument(item.preview, item.file)} title={item.file.name} style={{ color: '#0f172a', fontSize: '14px', fontWeight: 500, cursor: 'pointer', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {truncateFileName(item.file.name)}
                            </span>
                          </div>
                          {(!Verified || reSubmit) && (
                            <button
                              type="button"
                              onClick={() => {
                                const arr = [...panCardFiles];
                                arr.splice(index, 1);
                                setPanCardFiles(arr);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }}
                              title="Remove Document"
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}

                      {(!Verified || reSubmit) && (panCardPreviews.length + panCardFiles.length < 2) && (
                        <label
                          style={{
                            flex: '1 1 200px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px', backgroundColor: '#f8fafc',
                            cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                        >
                          <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                            <Upload size={20} color="#3b82f6" />
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#334155' }}>
                            Click to upload <span style={{ color: '#3b82f6' }}>PAN Card & GST</span>
                          </span>
                          <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>PNG, JPG (max. 5MB)</span>
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            const imageFiles = files.filter(file => {
                              if (!file.type.startsWith('image/')) {
                                toast.error(`${file.name} is not an image`);
                                return false;
                              }
                              return true;
                            });
                            const availableSlots = 2 - (panCardPreviews.length + panCardFiles.length);
                            const selectedFiles = imageFiles.slice(0, availableSlots);

                            const processedFiles = await Promise.all(selectedFiles.map(async (file) => {
                              if (file.size > 5 * 1024 * 1024) {
                                try {
                                  const compressedFile = await imageCompression(file, { maxSizeMB: 4.5, maxWidthOrHeight: 2048, useWebWorker: true });
                                  return { file: compressedFile, preview: URL.createObjectURL(compressedFile) };
                                } catch (error) {
                                  console.error("Compression error:", error);
                                  toast.error(`Failed to compress ${file.name}`);
                                }
                              }
                              return { file, preview: URL.createObjectURL(file) };
                            }));
                            setPanCardFiles(prev => [...prev, ...processedFiles]);
                            setErrors(prev => ({ ...prev, panCard: '' }));
                          }} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </>
            }

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={saveStoreInfo} disabled={loading || !storeInfoChanged} style={{ opacity: (loading || !storeInfoChanged) ? 0.6 : 1, cursor: (loading || !storeInfoChanged) ? 'not-allowed' : 'pointer' }}>
                {loading ? <div className="spinner" /> : <><Save size={18} /> Save Store Info</>}
              </button>
            </div>
          </div>
        )}

        {/* Location tab */}
        {tab === 'location' && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)',
              border: '1px solid #bae6fd', borderRadius: '16px', padding: '24px', marginBottom: '24px',
              display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'
            }}>
              <div style={{ width: '56px', height: '56px', background: '#14b8a6', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(20,184,166,0.2)', flexShrink: 0 }}>
                <MapPin size={28} color="white" />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>Store Location</h3>
                <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
                  {profile.businessAddress?.street || 'No Street Address'}
                  {profile.businessAddress?.villageOrLocality ? `, ${profile.businessAddress.villageOrLocality}` : ''}, {profile.businessAddress?.city || 'City'} <br />
                  {profile.businessAddress?.state || 'State'} - {profile.businessAddress?.pincode || 'Pincode'}
                </p>
              </div>
            </div>

            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>Edit Location Address</h4>
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              {/* Left Column: Form Fields */}
              <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

                <div>
                  <label className="form-label">Street Address <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className={`form-input ${errors.street ? 'input-error' : ''}`} style={errors.street ? { borderColor: '#ef4444' } : {}} name="address.street" value={profile.businessAddress?.street || ''} onChange={handleProfileChange} placeholder="Street / Door No." />
                  {errors.street && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.street}</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
                  <div>
                    <label className="form-label">Village / Locality <span style={{ color: '#ef4444' }}>*</span></label>
                    <input className={`form-input ${errors.villageOrLocality ? 'input-error' : ''}`} style={errors.villageOrLocality ? { borderColor: '#ef4444' } : {}} name="address.villageOrLocality" value={profile.businessAddress?.villageOrLocality || ''} onChange={handleProfileChange} placeholder="Village or Locality" />
                    {errors.villageOrLocality && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.villageOrLocality}</span>}
                  </div>
                  <div>
                    <label className="form-label">City <span style={{ color: '#ef4444' }}>*</span></label>
                    <input className={`form-input ${errors.city ? 'input-error' : ''}`} style={errors.city ? { borderColor: '#ef4444' } : {}} name="address.city" value={profile.businessAddress?.city || ''} onChange={handleProfileChange} placeholder="City" />
                    {errors.city && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.city}</span>}
                  </div>
                  <div>
                    <label className="form-label">State <span style={{ color: '#ef4444' }}>*</span></label>
                    <input className={`form-input ${errors.state ? 'input-error' : ''}`} style={errors.state ? { borderColor: '#ef4444' } : {}} name="address.state" value={profile.businessAddress?.state || ''} onChange={handleProfileChange} placeholder="State" />
                    {errors.state && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.state}</span>}
                  </div>
                  <div>
                    <label className="form-label">Pincode <span style={{ color: '#ef4444' }}>*</span></label>
                    <input className={`form-input ${errors.pincode ? 'input-error' : ''}`} style={errors.pincode ? { borderColor: '#ef4444' } : {}} name="address.pincode" value={profile.businessAddress?.pincode || ''} onChange={handleProfileChange} placeholder="6-digit pincode" />
                    {errors.pincode && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.pincode}</span>}
                  </div>
                </div>
                <div>
                  <label className="form-label">Address <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className={`form-input ${errors.fullAddress ? 'input-error' : ''}`} style={errors.fullAddress ? { borderColor: '#ef4444' } : {}} name="address.fullAddress" value={profile.businessAddress?.fullAddress || ''} onChange={handleProfileChange} placeholder="Enter full address" />
                  {errors.fullAddress && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.fullAddress}</span>}
                </div>
                {/* <div>
                  <label className="form-label">Temporary Location</label>
                  <input className="form-input" name="address.temporaryLocation" value={profile.businessAddress?.temporaryLocation || ''} onChange={handleProfileChange} placeholder="Temporary Location (if any)" />
                </div>
                <div>
                  <label className="form-label">Permanent Location</label>
                  <input className="form-input" name="address.permanentLocation" value={profile.businessAddress?.permanentLocation || ''} onChange={handleProfileChange} placeholder="Permanent Location" />
                </div> */}
              </div>

              {/* Right Column: Open Modal Button */}
              <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
                <div style={{ width: '48px', height: '48px', background: '#e0f2fe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <MapPin size={24} color="#0ea5e9" />
                </div>
                <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>Update Location Details</h4>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', maxWidth: '280px' }}>
                  Search for your address or accurately drop a pin on the map to set your store's location.
                </p>
                <button
                  onClick={() => setIsMapModalOpen(true)}
                  style={{
                    padding: '12px 24px', borderRadius: '10px', border: 'none', background: '#0ea5e9', color: 'white',
                    fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                    boxShadow: '0 4px 12px rgba(14, 165, 233, 0.25)', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(14, 165, 233, 0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.25)'; }}
                >
                  <Navigation size={18} /> Open Map to Update Location
                </button>
              </div>
            </div>

            {/* Save Location Button */}
            <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={saveLocation}
                disabled={loading || !locationChanged}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 28px', borderRadius: '10px', border: 'none', cursor: locationChanged ? 'pointer' : 'not-allowed',
                  background: locationChanged ? 'linear-gradient(135deg, #14b8a6, #0d9488)' : '#e2e8f0',
                  color: locationChanged ? 'white' : '#94a3b8',
                  fontWeight: '700', fontSize: '14px',
                  boxShadow: locationChanged ? '0 4px 14px rgba(20,184,166,0.35)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? <div className="spinner" /> : <><Save size={16} /> Save Location</>}
              </button>
            </div>
          </div>
        )}

        {/* Bank tab */}
        {tab === 'bank' && (
          <div>
            {/* Visual Bank Card */}
            <div style={{
              background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)',
              border: '1px solid #f5d0fe', borderRadius: '16px', padding: '24px', marginBottom: '24px',
              display: 'flex', alignItems: 'center', gap: '20px'
            }}>
              <div style={{ width: '56px', height: '56px', background: '#d946ef', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(217,70,239,0.2)', flexShrink: 0 }}>
                <CreditCard size={28} color="white" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                  {savedBank.bank_name || 'No Bank Added'}
                </h3>
                <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5', fontFamily: 'monospace' }}>
                  A/C: {savedBank.account_number ? '••••••••••' + String(savedBank.account_number).slice(-4) : '••••••••••'} <br />
                  Account Holder: {savedBank.account_holder_name || "-"} <br />
                </p>
              </div>
            </div>

            <div style={{ background: '#fffbeb', padding: '16px', borderRadius: '12px', border: '1px solid #fef3c7', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Lock size={20} color="#d97706" style={{ flexShrink: 0 }} />
              <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                <strong>Secure Storage:</strong> Your bank details are encrypted and securely stored. They are used exclusively for automated payouts. Never share your full account details or OTPs with anyone.
              </p>
            </div>

            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>Edit Payout Account</h4>
            <div className="profile-info-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {/* Highlighted IFSC Code Input */}
              <div style={{ gridColumn: '1 / -1', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '16px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <label className="form-label" style={{ color: '#166534', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <Search size={16} /> Enter IFSC Code First <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ position: 'relative', maxWidth: '350px' }}>
                  <input
                    className={`form-input ${errors.ifsc_code || ifscError ? 'input-error' : ''}`}
                    style={{
                      ...(errors.ifsc_code || ifscError ? { borderColor: '#ef4444' } : { borderColor: '#86efac' }),
                      background: '#fff',
                      fontSize: '16px',
                      padding: '12px 16px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}
                    name="ifsc_code"
                    value={bank.ifsc_code}
                    onChange={handleBankChange}
                    placeholder="e.g. SBIN0001234"
                    maxLength={11}
                  />
                  {isLoadingIfsc && <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', border: '2px solid #e2e8f0', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>}
                </div>
                {isLoadingIfsc && <div style={{ fontSize: '13px', color: '#059669', marginTop: '8px', fontWeight: '600' }}>Fetching bank details...</div>}
                {ifscError && <div style={{ fontSize: '13px', color: '#ef4444', marginTop: '8px', fontWeight: '600' }}>{ifscError}</div>}
                {errors.ifsc_code && !ifscError && <span style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px', display: 'block', fontWeight: '600' }}>{errors.ifsc_code}</span>}
              </div>

              <div>
                <label className="form-label">Bank Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input readOnly className={`form-input ${errors.bank_name ? 'input-error' : ''}`} style={errors.bank_name ? { borderColor: '#ef4444', background: '#f8fafc' } : { background: '#f8fafc' }} name="bank_name" value={bank.bank_name} onChange={handleBankChange} placeholder="Auto-filled from IFSC" />
                {errors.bank_name && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.bank_name}</span>}
              </div>

              <div>
                <label className="form-label">Account Holder Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className={`form-input ${errors.account_holder_name ? 'input-error' : ''}`} style={errors.account_holder_name ? { borderColor: '#ef4444' } : {}} name="account_holder_name" value={bank.account_holder_name} onChange={handleBankChange} placeholder="Full name as in bank" />
                {errors.account_holder_name && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.account_holder_name}</span>}
              </div>

              <div>
                <label className="form-label">Account Number <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input className={`form-input ${errors.account_number ? 'input-error' : ''}`} style={errors.account_number ? { borderColor: '#ef4444', paddingRight: '48px' } : { paddingRight: '48px' }} name="account_number" value={bank.account_number} onChange={handleBankChange} onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} type={showAccountNumber ? 'text' : 'password'} placeholder="Account number" />
                  <button type="button" onClick={() => setShowAccountNumber(!showAccountNumber)}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                    {showAccountNumber ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                  </button>
                </div>
                {errors.account_number && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.account_number}</span>}
              </div>

              <div>
                <label className="form-label">Confirm Account Number <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input className={`form-input ${errors.confirm_account_number ? 'input-error' : ''}`} style={errors.confirm_account_number ? { borderColor: '#ef4444', paddingRight: '48px' } : { paddingRight: '48px' }} name="confirm_account_number" value={bank.confirm_account_number} onChange={handleBankChange} onPaste={(e) => e.preventDefault()} onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onDrop={(e) => e.preventDefault()} type={showConfirmAccountNumber ? 'text' : 'password'} placeholder="Confirm account number" />
                  <button type="button" onClick={() => setShowConfirmAccountNumber(!showConfirmAccountNumber)}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                    {showConfirmAccountNumber ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                  </button>
                </div>
                {errors.confirm_account_number && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.confirm_account_number}</span>}
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={saveBankDetails} disabled={loading || !bankChanged} style={{ opacity: (loading || !bankChanged) ? 0.6 : 1, cursor: (loading || !bankChanged) ? 'not-allowed' : 'pointer' }}>
                {loading ? <div className="spinner" /> : <><Save size={18} /> Save Bank Details</>}
              </button>
            </div>
          </div>
        )}

        {/* Security tab */}
        {tab === 'security' && (
          <div>
            {/* Visual Security Card */}
            <div style={{
              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
              border: '1px solid #cbd5e1', borderRadius: '16px', padding: '24px', marginBottom: '24px',
              display: 'flex', alignItems: 'center', gap: '20px'
            }}>
              <div style={{ width: '56px', height: '56px', background: '#334155', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(51,65,85,0.2)', flexShrink: 0 }}>
                <Lock size={28} color="white" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                  Account Security
                </h3>
                <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
                  Manage your password and secure your account credentials. <br />
                  Ensure you use a strong, unique password to protect your store.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
              {/* Left Side: Info */}
              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Update Password</h4>
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6', marginBottom: '16px' }}>
                  To keep your account secure, we recommend changing your password regularly.
                  Your new password must be at least 8 characters long and should not be easily guessable.
                </p>
                <div style={{ background: '#f0f9ff', padding: '16px', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                  <p style={{ fontSize: '13px', color: '#0369a1', margin: 0 }}>
                    <strong>Tip:</strong> Use a combination of uppercase letters, lowercase letters, numbers, and symbols for maximum security.
                  </p>
                </div>
              </div>

              {/* Right Side: Form */}
              <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: '20px', background: '#fafafa', padding: '24px', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                <div>
                  <label className="form-label">Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showCurrentPassword ? 'text' : 'password'} className={`form-input ${errors.currentPassword ? 'input-error' : ''}`} value={passwords.currentPassword}
                      onChange={e => {
                        setPasswords(p => ({ ...p, currentPassword: e.target.value }));
                        setErrors(prev => ({ ...prev, currentPassword: '' }));
                      }} placeholder="Enter your current password" style={{ backgroundColor: 'white', paddingRight: '48px', ...(errors.currentPassword ? { borderColor: '#ef4444' } : {}) }} />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                      {showCurrentPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                    </button>
                  </div>
                  {errors.currentPassword && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.currentPassword}</span>}
                </div>

                <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>

                <div>
                  <label className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showNewPassword ? 'text' : 'password'} className="form-input" value={passwords.newPassword}
                      onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))} placeholder="Enter new password (Min. 8 chars)" style={{ backgroundColor: 'white', paddingRight: '48px' }} />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                      {showNewPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                    </button>
                  </div>


                </div>

                <div>
                  <label className="form-label">Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showConfirmPassword ? 'text' : 'password'} className="form-input" value={passwords.confirmPassword}
                      onChange={e => setPasswords(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="Re-type new password" style={{ backgroundColor: 'white', paddingRight: '48px' }} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                      {showConfirmPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <button className="btn-primary" onClick={savePassword} disabled={loading || !passwordChanged} style={{ width: '100%', justifyContent: 'center', opacity: (loading || !passwordChanged) ? 0.6 : 1, cursor: (loading || !passwordChanged) ? 'not-allowed' : 'pointer' }}>
                    {loading ? <div className="spinner" /> : <><Lock size={16} /> Change Password</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shipping Tab */}
        {tab === 'shipping' && (
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', paddingBottom: '40px' }}>

            {/* Store Shipping Rules Section */}
            <div style={{ flex: '1 1 500px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ textAlign: 'left' }}>
                <h4 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Free Delivery Configuration</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', maxWidth: '600px' }}>
                  Enable free shipping based on minimum order value or delivery radius (or both). These conditions will be checked during checkout to waive standard delivery fees.
                </p>
              </div>

              {/* Free Shipping by Min Order Value Card */}
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
                <div style={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isMinOrderEnabled ? '1px solid #e2e8f0' : 'none', background: isMinOrderEnabled ? '#f8fafc' : '#fff', transition: 'all 0.3s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: isMinOrderEnabled ? '#ccfbf1' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isMinOrderEnabled ? '#0f766e' : '#64748b', transition: 'all 0.3s ease' }}>
                      <ShoppingCart size={24} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Minimum Order Value</h4>
                      <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>{isMinOrderEnabled ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    <div style={{ position: 'relative', width: '52px', height: '30px' }}>
                      <input type="checkbox" style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} checked={isMinOrderEnabled} onChange={handleToggleMinOrder} />
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: isMinOrderEnabled ? '#14b8a6' : '#cbd5e1', borderRadius: '9999px', transition: 'background-color 0.3s ease' }}></div>
                      <div style={{ position: 'absolute', left: isMinOrderEnabled ? '24px' : '2px', top: '2px', background: 'white', width: '26px', height: '26px', borderRadius: '50%', transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}></div>
                    </div>
                  </label>
                </div>

                <div>
                  <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                        Minimum Order Value
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '600', fontSize: '15px' }}>₹</span>
                        <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} className="form-input" name="freeShippingMinOrderValue" value={(shippingRules.freeShippingMinOrderValue !== undefined && shippingRules.freeShippingMinOrderValue >= 0) ? shippingRules.freeShippingMinOrderValue : ''} onChange={handleShippingRulesChange} placeholder="e.g. 1000" style={{ paddingLeft: '32px', fontSize: '15px', height: '48px', borderRadius: '10px' }} />
                      </div>
                      <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', marginBottom: 0 }}>Customers must spend at least this amount to unlock free shipping.</p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <button className="btn-primary" onClick={saveMinOrderRule} disabled={loadingMinOrder || !minOrderChanged} style={{ padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex', gap: '8px', alignItems: 'center', opacity: (loadingMinOrder || !minOrderChanged) ? 0.6 : 1, cursor: (loadingMinOrder || !minOrderChanged) ? 'not-allowed' : 'pointer' }}>
                        {loadingMinOrder ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> : <><Save size={16} /> Save Settings</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Free Shipping by Max Radius Card */}
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
                <div style={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isMaxDistanceEnabled ? '1px solid #e2e8f0' : 'none', background: isMaxDistanceEnabled ? '#f8fafc' : '#fff', transition: 'all 0.3s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: isMaxDistanceEnabled ? '#ccfbf1' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isMaxDistanceEnabled ? '#0f766e' : '#64748b', transition: 'all 0.3s ease' }}>
                      <Map size={24} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Maximum Delivery Radius</h4>
                      <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>{isMaxDistanceEnabled ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    <div style={{ position: 'relative', width: '52px', height: '30px' }}>
                      <input type="checkbox" style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} checked={isMaxDistanceEnabled} onChange={handleToggleMaxDistance} />
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: isMaxDistanceEnabled ? '#14b8a6' : '#cbd5e1', borderRadius: '9999px', transition: 'background-color 0.3s ease' }}></div>
                      <div style={{ position: 'absolute', left: isMaxDistanceEnabled ? '24px' : '2px', top: '2px', background: 'white', width: '26px', height: '26px', borderRadius: '50%', transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}></div>
                    </div>
                  </label>
                </div>

                <div>
                  <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                        Maximum Delivery Radius (km)
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} className="form-input" name="freeShippingMaxDistance" value={(shippingRules.freeShippingMaxDistance !== undefined && shippingRules.freeShippingMaxDistance >= 0) ? shippingRules.freeShippingMaxDistance : ''} onChange={handleShippingRulesChange} placeholder="e.g. 10" style={{ paddingRight: '44px', fontSize: '15px', height: '48px', borderRadius: '10px' }} />
                        <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '600', fontSize: '13px' }}>KM</span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', marginBottom: 0 }}>Free shipping will only apply if the customer is within this distance.</p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <button className="btn-primary" onClick={saveMaxDistanceRule} disabled={loadingMaxDistance || !maxDistanceChanged} style={{ padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex', gap: '8px', alignItems: 'center', opacity: (loadingMaxDistance || !maxDistanceChanged) ? 0.6 : 1, cursor: (loadingMaxDistance || !maxDistanceChanged) ? 'not-allowed' : 'pointer' }}>
                        {loadingMaxDistance ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> : <><Save size={16} /> Save Settings</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Days Section */}
            <div style={{ flex: '1 1 500px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ textAlign: 'left' }}>
                <h4 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Delivery Days Configuration</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', maxWidth: '600px' }}>
                  Set the number of days it takes for your products to be delivered.
                </p>
              </div>

              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                      Delivery Days
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input type="number" min="1" max={platformRateCard.maxDeliveryDays || 2} onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === '0' && e.currentTarget.value === '') e.preventDefault(); }} className="form-input" name="deliveryDays" value={shippingRules.deliveryDays ?? ''} onChange={handleShippingRulesChange} placeholder={`Max ${platformRateCard.maxDeliveryDays || 2}`} style={{ paddingRight: '54px', fontSize: '15px', height: '48px', borderRadius: '10px' }} />
                      <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '600', fontSize: '13px' }}>Days</span>
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', marginBottom: 0 }}>
                      This will be displayed to the customer as the estimated delivery time. (Max: {platformRateCard.maxDeliveryDays || 2} Days)
                    </p>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                    <button className="btn-primary" onClick={saveShippingRules} disabled={loading} style={{ padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex', gap: '8px', alignItems: 'center', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                      {loading ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> : <><Save size={16} /> Save Delivery Days</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Rate Card Section */}
            <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ textAlign: 'left' }}>
                <h4 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>Platform Rate Card</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', maxWidth: '600px' }}>
                  If an order does not qualify for free shipping, the customer will be automatically charged these distance-based delivery fees.
                </p>
              </div>

              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
                <div style={{ background: '#f8fafc', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                  <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Map size={18} color="#64748b" /> Distance Tiers & Weight Fees
                  </h5>
                </div>
                <div style={{ padding: '0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Base Delivery Fee</span>
                    <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>₹{platformRateCard.shippingBaseFee}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>0 to 5 km Rate (Per KM)</span>
                    <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>₹{platformRateCard.shippingTier0_5}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>5.1 to 10 km Rate (Per KM)</span>
                    <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>₹{platformRateCard.shippingTier5_10}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Above 10 km Rate (Per KM)</span>
                    <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>₹{platformRateCard.shippingTier10Plus}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', background: '#f8fafc' }}>
                    <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Heavy Item Fee (Per KG)</span>
                    <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>+ ₹{platformRateCard.shippingPerKgRate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Profile Image Modal */}
      {isImageModalOpen && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 99999, backdropFilter: 'blur(1px)'
        }}>
          <div style={{
            background: 'white', padding: '24px', borderRadius: '16px',
            width: '320px', minHeight: '340px', display: 'flex', flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a', textAlign: 'center' }}>Update Profile Image</h3>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0, marginBottom: '20px' }}>
              <div style={{
                width: '120px', height: '120px', borderRadius: '50%',
                border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center',
                justifyContent: 'center', overflow: 'hidden', position: 'relative',
                background: '#f8fafc'
              }}>
                {tempLogoPreview ? (
                  <img src={tempLogoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Camera size={32} color="#94a3b8" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be less than 5MB');
                        return;
                      }
                      setTempLogoFile(file);
                      setTempLogoPreview(URL.createObjectURL(file));
                    }
                  }}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  title="Select Image"
                />
              </div>
              <p style={{ textAlign: 'center', fontSize: '12px', color: '#64748b', margin: '16px 0 0 0' }}>
                Click circle to select image (max 5MB)
              </p>

              {logoPreview && (
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await sellerAPI.removeProfileImage();
                      dispatch(updateSellerProfile({ ...seller, storeLogo: '' }));
                      setLogoPreview('');
                      setLogoFile(null);
                      setTempLogoFile(null);
                      setTempLogoPreview('');
                      Swal.fire({
                        icon: 'success',
                        title: 'Removed',
                        text: 'Profile image removed successfully',
                        confirmButtonColor: '#14b8a6'
                      });
                      setIsImageModalOpen(false);
                    } catch (err) {
                      toast.error(err?.response?.data?.message || 'Failed to remove image');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  style={{
                    background: 'none', border: 'none', color: '#ef4444', fontSize: '13px', fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', borderRadius: '6px', transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                >
                  {loading ? <div className="spinner" style={{ width: '14px', height: '14px', borderColor: '#ef4444', borderTopColor: 'transparent' }} /> : <><Trash2 size={14} /> Remove Current Picture</>}
                </button>
              )}
            </div>

            <div style={{ marginTop: 'auto' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setIsImageModalOpen(false);
                    setTempLogoFile(null);
                    setTempLogoPreview('');
                  }}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1',
                    background: 'white', color: '#475569', fontSize: '14px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (tempLogoFile) {
                      setLogoFile(tempLogoFile);
                      setLogoPreview(tempLogoPreview);

                      setLoading(true);
                      try {
                        const fd = new FormData();
                        fd.append('userid', seller?._id || seller?.id);
                        fd.append('storeLogo', tempLogoFile);

                        const res = await sellerAPI.uploadProfileImage(fd);
                        dispatch(updateSellerProfile(res.data.seller));
                        Swal.fire({
                          icon: 'success',
                          title: 'Success',
                          text: 'image upload successfully',
                          confirmButtonColor: '#14b8a6'
                        });
                        setIsImageModalOpen(false);
                      } catch (err) {
                        toast.error(err?.response?.data?.message || 'Failed to save image');
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      toast.error('Please select an image first');
                    }
                  }}
                  disabled={loading || !tempLogoFile}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                    background: tempLogoFile ? '#14b8a6' : '#94a3b8', color: 'white',
                    fontSize: '14px', fontWeight: '600', cursor: (loading || !tempLogoFile) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  {loading ? <div className="spinner" style={{ width: '16px', height: '16px', borderColor: 'white', borderTopColor: 'transparent' }} /> : <><Save size={16} />Save</>}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Map Selection Modal Popup */}
      {isMapModalOpen && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '20px',
          animation: 'fadeIn 0.2s ease-out',
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '24px',
            width: '100%', maxWidth: '600px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#fafbfc'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(20,184,166,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Map size={18} color="#0d9488" />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    Select Shop Location
                  </h3>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>
                    Drag the map or search to choose location
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsMapModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content / Map */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Search Box */}
              {isLoaded && (
                <StandaloneSearchBox
                  onLoad={ref => (searchBoxRef.current = ref)}
                  onPlacesChanged={onPlacesChanged}
                >
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                      placeholder="Search for an area, city or pincode..."
                      style={{
                        width: '100%', padding: '11px 36px 11px 38px',
                        borderRadius: '10px', border: '2px solid #e2e8f0',
                        fontSize: '13px', fontWeight: '500', color: '#0f172a',
                        outline: 'none', background: '#f8fafc',
                        transition: 'all 0.2s', boxSizing: 'border-box'
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = '#fff'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                    />
                    {searchValue && (
                      <button
                        onClick={() => {
                          setSearchValue('');
                          if (searchInputRef.current) searchInputRef.current.focus();
                        }}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </StandaloneSearchBox>
              )}

              {/* Map container */}
              <div style={{
                borderRadius: '16px', overflow: 'hidden',
                border: '2px solid #e2e8f0', height: '380px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                position: 'relative'
              }}>
                {!isLoaded ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: '10px' }}>
                    <div className="spinner" style={{ width: '28px', height: '28px', borderColor: '#14b8a6', borderTopColor: 'transparent' }} />
                    <p style={{ color: '#64748b', fontSize: '13px' }}>Loading Google Maps...</p>
                  </div>
                ) : (
                  <>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={mapCenter}
                      zoom={16}
                      options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: true, mapTypeControlOptions: { position: window.google?.maps?.ControlPosition?.TOP_RIGHT } }}
                      onLoad={(map) => { mapInstanceRef.current = map; }}
                      onIdle={() => {
                        if (mapIdleDebounceRef.current) clearTimeout(mapIdleDebounceRef.current);
                        mapIdleDebounceRef.current = setTimeout(() => {
                          const map = mapInstanceRef.current;
                          if (map) {
                            const center = map.getCenter();
                            if (center) {
                              const lat = parseFloat(center.lat().toFixed(6));
                              const lng = parseFloat(center.lng().toFixed(6));
                              const last = lastGeocodedPosRef.current;
                              if (last && Math.abs(last.lat - lat) < 0.00005 && Math.abs(last.lng - lng) < 0.00005) return;
                              lastGeocodedPosRef.current = { lat, lng };
                              setMapCenter({ lat, lng });
                              reverseGeocode(lat, lng);
                            }
                          }
                        }, 600);
                      }}
                    />
                    {/* Fixed Center Crosshair Pin overlay */}
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 10,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                    }}>
                      <div style={{
                        width: '40px', height: '40px', background: '#ef4444',
                        borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)',
                        border: '3px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <div style={{ width: '12px', height: '12px', background: '#fff', borderRadius: '50%', transform: 'rotate(45deg)' }} />
                      </div>
                    </div>
                  </>
                )}

                {/* Move instruction overlay */}
                <div style={{
                  position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(4px)',
                  color: '#fff', padding: '8px 16px', borderRadius: '20px',
                  fontSize: '12px', fontWeight: '600', pointerEvents: 'none',
                  whiteSpace: 'nowrap', zIndex: 10,
                }}>
                  📍 Drag the map to position the pin on your shop
                </div>
              </div>

              {/* GPS inside modal */}
              <button
                type="button"
                onClick={getCurrentLocation}
                disabled={locating || !isLoaded}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1.5px dashed #14b8a6', background: 'rgba(20,184,166,0.02)',
                  color: '#0d9488', fontSize: '12px', fontWeight: '700',
                  cursor: (locating || !isLoaded) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                {locating ? <><div className="spinner" style={{ width: '14px', height: '14px', borderColor: '#0d9488', borderTopColor: 'transparent' }} /> Detecting...</> : <><Navigation size={14} /> Center on My Current Location</>}
              </button>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'flex-end', gap: '12px',
              background: '#fafbfc'
            }}>
              <button
                onClick={() => setIsMapModalOpen(false)}
                style={{
                  padding: '10px 18px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#fff',
                  color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsMapModalOpen(false);
                  toast.success("Location updated successfully!");
                }}
                style={{
                  padding: '10px 22px', borderRadius: '10px', border: 'none', background: '#14b8a6',
                  color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(20,184,166,0.25)',
                }}
              >
                Confirm Location
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <style>{`
        .pac-container {
          z-index: 10000 !important;
          border-radius: 12px !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.1) !important;
          margin-top: 6px !important;
          font-family: 'Poppins', sans-serif !important;
        }
        .pac-item { padding: 10px 14px !important; font-size: 13px !important; cursor: pointer !important; }
        .pac-item:hover { background: #f0fdfa !important; }
        .pac-item-query { color: #0f172a !important; font-weight: 600 !important; }
      `}</style>
    </div>
  );
}
